import _mapValues from 'lodash/mapValues';
import _parseInt from 'lodash/parseInt';
import _reduce from 'lodash/reduce';
import _set from 'lodash/set';
import _toFinite from 'lodash/toFinite';
import _transform from 'lodash/transform';
import APPCONFIG from '../../../constants/Config';
import {
  REINIT_STATE,
  REINIT_AUTH,
  CPM_RESET_CARPARK_CACHE,
  CPM_GET_CARPARKS_LIST,
  CPM_COUNT_CARPARKS,
  CPM_GET_CARPARK,
  CPM_GET_CARPARK_BY_SLUG,
  CPM_GET_CARPARK_DEFAULT_SLUG,
  CPM_GET_CARPARK_SYNCHRONIZATION_STATUSES,
  CPM_GET_PRICE_RULES,
  CPM_UPDATE_CARPARK,
  CPM_UPDATE_CARPARK_CONTACT,
  CPM_GET_CARPARK_PLANNINGS,
  CPM_GET_CARPARK_PARKING_CATEGORIES,
  CPM_GET_CARPARK_PRICE_GRIDS,
  CPM_TRIGGER_CARPARK_DOOR,
  CPM_GET_CARPARK_UNIQUE_KEYPAD_CODE,
  CPM_GET_CARPARK_REVENUES_SUMMARY,
  CPM_UPDATE_CARPARK_BOOKING_SETTINGS,
  CPM_UPDATE_CARPARK_GENERAL_SETTINGS,
  CPM_UPDATE_CARPARK_ON_DEMAND_SETTINGS,
  CPM_UPDATE_CARPARK_LICENSE_PLATE_SETTINGS,
  CPM_UPDATE_CARPARK_SPACES_SETTINGS,
  CPM_UPDATE_CARPARK_FINANCIAL_INFORMATION,
  CPM_UPDATE_CARPARK_WORKSPACE_GROUPS,
  CPM_UPDATE_CARPARK_PARTNER,
  CPM_GET_CARPARK_WORKSPACE_GROUPS,
} from '../../../constants/ActionTypes';
// Models
/* eslint-disable import/named */
import { deserialize, deserializeCarparkPublic } from '../../../models/cpm/carpark';
import { deserialize as deserializePriceRule } from '../../../models/cpm/priceRule';
import { deserialize as deserializeSlug } from '../../../models/cpm/slug';
import { deserialize as deserializeOperation } from '../../../models/cpm/synchronization';
import { deserializeRevenues } from '../../../models/cpm/metric';
import { deserialize as deserializeWorkspaceGroup } from '../../../models/cpm/workspaceGroup';

/* eslint-enable import/named */

const initialSettings = APPCONFIG.cpmCarpark;
const carparkReducer = (state = initialSettings, action) => {
  let key;
  let mutationResult;
  let metrics;
  switch (action.type) {
    // REINIT STATE
    case REINIT_STATE:
      key = null;
      if (action.target === 'fullCarpark' || action.target === 'currentCarpark') {
        return {
          ...state,
          cache: { ...initialSettings.cache },
          current: { ...initialSettings.current },
          defaultSlug: { ...initialSettings.defaultSlug },
          update: { ...initialSettings.update },
          synchronizationStatuses: { ...initialSettings.synchronizationStatuses },
          triggerDoor: { ...initialSettings.triggerDoor },
          uniqueKeypadCode: { ...initialSettings.uniqueKeypadCode },
          updateBookingSettings: { ...initialSettings.updateBookingSettings },
          updateGeneralSettings: { ...initialSettings.updateGeneralSettings },
          updateOnDemandSettings: { ...initialSettings.updateOnDemandSettings },
          updateLicensePlateSettings: { ...initialSettings.updateLicensePlateSettings },
          updateSpacesSettings: { ...initialSettings.updateSpacesSettings },
          updateFinancialInformation: { ...initialSettings.updateFinancialInformation },
        };
      }
      switch (action.target) {
        case 'updateCarpark':
          key = 'update';
          break;
        case 'updateCarparkBookingSettings':
          key = 'updateBookingSettings';
          break;
        case 'updateCarparkGeneralSettings':
          key = 'updateGeneralSettings';
          break;
        case 'updateCarparkLicensePlateSettings':
          key = 'updateLicensePlateSettings';
          break;
        case 'updateCarparkOnDemandSettings':
          key = 'updateOnDemandSettings';
          break;
        case 'updateCarparkSpacesSettings':
          key = 'updateSpacesSettings';
          break;
        case 'updateCarparkFinancialInformation':
          key = 'updateFinancialInformation';
          break;
        case 'listCarparks':
          key = 'list';
          break;
        default:
          break;
      }
      if (key) {
        return {
          ...state,
          [key]: {
            ...initialSettings[key],
          },
        };
      }
      return state;
    // REINIT AUTH
    case REINIT_AUTH:
      return {
        ...state,
        current: { ...initialSettings.current },
      };
    // CACHE MANAGEMENT
    case CPM_RESET_CARPARK_CACHE:
      return {
        ...state,
        cache: _transform(state.cache, (result, value, key) => { // eslint-disable-line no-shadow
          _set(result, key, false);
          return result;
        }, {}),
      };
    case `${CPM_GET_CARPARK_PLANNINGS}_SUCCESS`:
    case `${CPM_GET_CARPARK_PARKING_CATEGORIES}_SUCCESS`:
    case `${CPM_GET_CARPARK_PRICE_GRIDS}_SUCCESS`:
      key = null;
      switch (action.type) {
        case `${CPM_GET_CARPARK_PLANNINGS}_SUCCESS`:
          key = 'plannings';
          break;
        case `${CPM_GET_CARPARK_PARKING_CATEGORIES}_SUCCESS`:
          key = 'parkingCategories';
          break;
        case `${CPM_GET_CARPARK_PRICE_GRIDS}_SUCCESS`:
          key = 'priceGrids';
          break;
        default:
          break;
      }
      if (key) {
        return {
          ...state,
          cache: {
            ...state.cache,
            [key]: true,
          },
        };
      }
      return state;
    // LIST
    case CPM_GET_CARPARKS_LIST:
    case CPM_COUNT_CARPARKS:
      return {
        ...state,
        list: {
          ...state.list,
          loading: true,
          error: null,
          elements: {
            ...state.list.elements,
            data: null,
          },
        },
      };
    case `${CPM_GET_CARPARKS_LIST}_ERROR`:
    case `${CPM_COUNT_CARPARKS}_ERROR`:
      return {
        ...state,
        list: {
          ...state.list,
          loading: false,
          error: action.error,
        },
      };
    case `${CPM_GET_CARPARKS_LIST}_SUCCESS`:
      return {
        ...state,
        list: {
          ...state.list,
          loading: false,
          elements: {
            ...state.list.elements,
            data: deserialize(action.data.listCarparks.entries),
            pageNumber: _parseInt(action.data.listCarparks.pageNumber),
            pageSize: _parseInt(action.data.listCarparks.pageSize),
            totalEntries: _parseInt(action.data.listCarparks.totalEntries),
            totalPages: _parseInt(action.data.listCarparks.totalPages),
          },
          count: action.count ? _parseInt(action.data.listCarparks.totalEntries) : state.list.count,
        },
      };
    case `${CPM_COUNT_CARPARKS}_SUCCESS`:
      return {
        ...state,
        list: {
          ...state.list,
          loading: false,
          elements: {
            ...state.list.elements,
            totalEntries: _parseInt(action.data.listCarparks.totalEntries),
          },
          count: action.count ? _parseInt(action.data.listCarparks.totalEntries) : state.list.count,
        },
      };
    // GET
    case CPM_GET_CARPARK:
      return {
        ...state,
        cache: {
          ...initialSettings.cache,
        },
        current: {
          ...state.current,
          loading: true,
          data: null,
          error: null,
        },
      };
    case `${CPM_GET_CARPARK}_ERROR`:
      return {
        ...state,
        current: {
          ...state.current,
          loading: false,
          error: action.error,
        },
      };
    case `${CPM_GET_CARPARK}_SUCCESS`:
      return {
        ...state,
        current: {
          ...state.current,
          loading: false,
          data: {
            ...state.current.data,
            ...deserialize(action.data.getCarpark),
          },
        },
      };
    // GET CARPARK BY TOKEN
    case CPM_GET_CARPARK_BY_SLUG:
      return {
        ...state,
        public: {
          ...state.public,
          loading: true,
        },
      };
    case `${CPM_GET_CARPARK_BY_SLUG}_ERROR`:
      return {
        ...state,
        public: {
          ...state.public,
          loading: false,
          error: action.error,
        },
      };
    case `${CPM_GET_CARPARK_BY_SLUG}_SUCCESS`:
      return {
        ...state,
        public: {
          ...state.public,
          loading: false,
          data: deserializeCarparkPublic(action.data.getCarparkBySlug),
        },
      };
    // CPM_GET_CARPARK_SYNCHRONIZATION_STATUSES
    case CPM_GET_CARPARK_SYNCHRONIZATION_STATUSES:
      return {
        ...state,
        synchronizationStatuses: {
          ...state.synchronizationStatuses,
          error: null,
          loading: true,
        },
      };
    case `${CPM_GET_CARPARK_SYNCHRONIZATION_STATUSES}_ERROR`:
      return {
        ...state,
        synchronizationStatuses: {
          ...state.synchronizationStatuses,
          error: action.error,
          loading: false,
        },
      };
    case `${CPM_GET_CARPARK_SYNCHRONIZATION_STATUSES}_SUCCESS`:
      return {
        ...state,
        synchronizationStatuses: {
          ...state.synchronizationStatuses,
          data: deserialize(action.data.getCarpark).synchronizationStatuses,
          loading: false,
        },
      };
    // GET PRICE RULES
    case `${CPM_GET_PRICE_RULES}_SUCCESS`:
      return {
        ...state,
        current: {
          ...state.current,
          data: {
            ...state.current.data,
            priceRules: deserializePriceRule(action.data.listPriceRules),
          },
        },
      };
    // UPDATE
    case CPM_UPDATE_CARPARK:
    case CPM_UPDATE_CARPARK_CONTACT:
      return {
        ...state,
        update: {
          ...state.update,
          updated: false,
          error: null,
        },
      };
    case `${CPM_UPDATE_CARPARK}_ERROR`:
    case `${CPM_UPDATE_CARPARK_CONTACT}_ERROR`:
      return {
        ...state,
        update: {
          ...state.update,
          error: action.error,
        },
      };
    case `${CPM_UPDATE_CARPARK}_SUCCESS`:
    case `${CPM_UPDATE_CARPARK_CONTACT}_SUCCESS`:
      return {
        ...state,
        update: {
          ...state.update,
          updated: true,
        },
      };
    // TRIGGER CARPARK DOOR
    case CPM_TRIGGER_CARPARK_DOOR:
      return {
        ...state,
        triggerDoor: {
          ...state.triggerDoor,
          done: false,
          error: null,
          result: null,
        },
      };
    case `${CPM_TRIGGER_CARPARK_DOOR}_ERROR`:
      return {
        ...state,
        triggerDoor: {
          ...state.triggerDoor,
          error: action.error,
        },
      };
    case `${CPM_TRIGGER_CARPARK_DOOR}_SUCCESS`:
      mutationResult = action.data.triggerDoor;
      return {
        ...state,
        triggerDoor: {
          ...state.triggerDoor,
          done: true,
          result: {
            ...mutationResult,
            id: _toFinite(mutationResult.id),
            operation: deserializeOperation(mutationResult.operation),
          },
        },
      };
    // GET UNIQUE CODE
    case CPM_GET_CARPARK_UNIQUE_KEYPAD_CODE:
      return {
        ...state,
        uniqueKeypadCode: {
          ...state.uniqueKeypadCode,
          loading: true,
          error: null,
          result: null,
        },
      };
    case `${CPM_GET_CARPARK_UNIQUE_KEYPAD_CODE}_ERROR`:
      return {
        ...state,
        uniqueKeypadCode: {
          ...state.uniqueKeypadCode,
          loading: false,
          error: action.error,
        },
      };
    case `${CPM_GET_CARPARK_UNIQUE_KEYPAD_CODE}_SUCCESS`:
      return {
        ...state,
        uniqueKeypadCode: {
          ...state.uniqueKeypadCode,
          loading: false,
          result: action.data.getUniqueKeypadCode,
        },
      };
    // GET REVENUES SUMMARY
    case CPM_GET_CARPARK_REVENUES_SUMMARY:
      return {
        ...state,
        revenue: {
          ...state.revenue,
          [action.cbParams.scope]: {
            loading: true,
            value: null,
          },
        },
      };
    case `${CPM_GET_CARPARK_REVENUES_SUMMARY}_ERROR`:
      return {
        ...state,
        revenue: {
          ...state.revenue,
          [action.scope]: {
            loading: false,
            value: null,
          },
        },
      };
    case `${CPM_GET_CARPARK_REVENUES_SUMMARY}_SUCCESS`:
      metrics = deserializeRevenues(action.data.metricsRevenues);
      return {
        ...state,
        revenue: {
          ...state.revenue,
          [action.scope]: {
            loading: false,
            value: _reduce(
              metrics,
              (result, { value }) => {
                const newResult = _mapValues(value, (v, k) => v + (result[k] || 0));
                return newResult;
              },
              {},
            ),
          },
        },
      };

    // UPDATE BOOKINGS SETTINGS
    case CPM_UPDATE_CARPARK_BOOKING_SETTINGS:
      return {
        ...state,
        updateBookingSettings: {
          ...state.updateBookingSettings,
          updated: false,
          error: null,
          result: null,
        },
      };
    case `${CPM_UPDATE_CARPARK_BOOKING_SETTINGS}_ERROR`:
      return {
        ...state,
        updateBookingSettings: {
          ...state.updateBookingSettings,
          error: action.error,
        },
      };
    case `${CPM_UPDATE_CARPARK_BOOKING_SETTINGS}_SUCCESS`:
      mutationResult = action.data.updateCarparkBookingSettings;
      return {
        ...state,
        updateBookingSettings: {
          ...state.updateBookingSettings,
          updated: true,
          result: {
            operation: deserializeOperation(mutationResult),
          },
        },
      };

    // UPDATE GENERAL SETTINGS
    case CPM_UPDATE_CARPARK_GENERAL_SETTINGS:
      return {
        ...state,
        updateGeneralSettings: {
          ...state.updateGeneralSettings,
          updated: false,
          error: null,
          result: null,
        },
      };
    case `${CPM_UPDATE_CARPARK_GENERAL_SETTINGS}_ERROR`:
      return {
        ...state,
        updateGeneralSettings: {
          ...state.updateGeneralSettings,
          error: action.error,
        },
      };
    case `${CPM_UPDATE_CARPARK_GENERAL_SETTINGS}_SUCCESS`:
      mutationResult = action.data.updateCarparkGeneralSettings;
      return {
        ...state,
        updateGeneralSettings: {
          ...state.updateGeneralSettings,
          updated: true,
          result: {
            operation: deserializeOperation(mutationResult),
          },
        },
      };

    // UPDATE ON DEMAND SETTINGS
    case CPM_UPDATE_CARPARK_ON_DEMAND_SETTINGS:
      return {
        ...state,
        updateOnDemandSettings: {
          ...state.updateOnDemandSettings,
          updated: false,
          error: null,
          result: null,
        },
      };
    case `${CPM_UPDATE_CARPARK_ON_DEMAND_SETTINGS}_ERROR`:
      return {
        ...state,
        updateOnDemandSettings: {
          ...state.updateOnDemandSettings,
          error: action.error,
        },
      };
    case `${CPM_UPDATE_CARPARK_ON_DEMAND_SETTINGS}_SUCCESS`:
      mutationResult = action.data.updateCarparkOnDemandSettings;
      return {
        ...state,
        updateOnDemandSettings: {
          ...state.updateOnDemandSettings,
          updated: true,
          result: {
            operation: deserializeOperation(mutationResult),
          },
        },
      };

    // UPDATE LICENSE PLATE READING SETTINGS
    case CPM_UPDATE_CARPARK_LICENSE_PLATE_SETTINGS:
      return {
        ...state,
        updateLicensePlateSettings: {
          ...state.updateLicensePlateSettings,
          updated: false,
          error: null,
        },
      };
    case `${CPM_UPDATE_CARPARK_LICENSE_PLATE_SETTINGS}_ERROR`:
      return {
        ...state,
        updateLicensePlateSettings: {
          ...state.updateLicensePlateSettings,
          error: action.error,
        },
      };
    case `${CPM_UPDATE_CARPARK_LICENSE_PLATE_SETTINGS}_SUCCESS`:
      mutationResult = action.data.updateCarparkOnDemandSettings;
      return {
        ...state,
        updateLicensePlateSettings: {
          ...state.updateLicensePlateSettings,
          updated: true,
        },
      };

    // UPDATE PARKING SPACES
    case CPM_UPDATE_CARPARK_SPACES_SETTINGS:
      return {
        ...state,
        updateSpacesSettings: {
          ...state.updateSpacesSettings,
          updated: false,
          error: null,
          result: null,
        },
      };
    case `${CPM_UPDATE_CARPARK_SPACES_SETTINGS}_ERROR`:
      return {
        ...state,
        updateSpacesSettings: {
          ...state.updateSpacesSettings,
          error: action.error,
        },
      };
    case `${CPM_UPDATE_CARPARK_SPACES_SETTINGS}_SUCCESS`:
      mutationResult = action.data.updateCarparkSpacesSettings;
      return {
        ...state,
        updateSpacesSettings: {
          ...state.updateSpacesSettings,
          updated: true,
          result: {
            operation: deserializeOperation(mutationResult),
          },
        },
      };

    // UPDATE FINANCIAL INFORMATION
    case CPM_UPDATE_CARPARK_FINANCIAL_INFORMATION:
    case CPM_UPDATE_CARPARK_PARTNER:
      return {
        ...state,
        updateFinancialInformation: {
          ...state.updateFinancialInformation,
        },
      };
    case `${CPM_UPDATE_CARPARK_FINANCIAL_INFORMATION}_ERROR`:
    case `${CPM_UPDATE_CARPARK_PARTNER}_ERROR`:
      return {
        ...state,
        updateFinancialInformation: {
          ...state.updateFinancialInformation,
          error: action.error,
        },
      };
    case `${CPM_UPDATE_CARPARK_FINANCIAL_INFORMATION}_SUCCESS`:
    case `${CPM_UPDATE_CARPARK_PARTNER}_SUCCESS`:
      return {
        ...state,
        updateFinancialInformation: {
          ...state.updateFinancialInformation,
          updated: true,
        },
      };

    // CPM_UPDATE_CARPARK_WORKSPACE_GROUPS
    case CPM_UPDATE_CARPARK_WORKSPACE_GROUPS:
      return {
        ...state,
        updateWorkspaceGroups: {
          ...state.updateWorkspaceGroups,
          updated: false,
          error: null,
          result: null,
        },
      };
    case `${CPM_UPDATE_CARPARK_WORKSPACE_GROUPS}_ERROR`:
      return {
        ...state,
        updateWorkspaceGroups: {
          ...state.updateWorkspaceGroups,
          error: action.error,
        },
      };
    case `${CPM_UPDATE_CARPARK_WORKSPACE_GROUPS}_SUCCESS`:
      return {
        ...state,
        updateWorkspaceGroups: {
          ...state.updateWorkspaceGroups,
          updated: true,
        },
      };
    // CPM_GET_CARPARK_WORKSPACE_GROUPS
    case CPM_GET_CARPARK_WORKSPACE_GROUPS:
      return {
        ...state,
        listWorkspaceGroups: {
          ...state.listWorkspaceGroups,
          loading: true,
          error: null,
          elements: {
            ...state.listWorkspaceGroups.elements,
            data: null,
          },
        },
      };
    case `${CPM_GET_CARPARK_WORKSPACE_GROUPS}_ERROR`:
      return {
        ...state,
        listWorkspaceGroups: {
          ...state.listWorkspaceGroups,
          loading: false,
          error: action.error,
        },
      };
    case `${CPM_GET_CARPARK_WORKSPACE_GROUPS}_SUCCESS`:
      return {
        ...state,
        listWorkspaceGroups: {
          ...state.listWorkspaceGroups,
          loading: false,
          elements: {
            ...state.listWorkspaceGroups.elements,
            data: deserializeWorkspaceGroup(action.data.listWorkspaceGroups.entries),
            pageNumber: _parseInt(action.data.listWorkspaceGroups.pageNumber),
            pageSize: _parseInt(action.data.listWorkspaceGroups.pageSize),
            totalEntries: _parseInt(action.data.listWorkspaceGroups.totalEntries),
            totalPages: _parseInt(action.data.listWorkspaceGroups.totalPages),
          },
        },
      };
    // CPM_GET_CARPARK_DEFAULT_SLUG
    case CPM_GET_CARPARK_DEFAULT_SLUG:
      return {
        ...state,
        defaultSlug: {
          ...state.defaultSlug,
          error: null,
          loading: true,
          result: null,
        },
      };
    case `${CPM_GET_CARPARK_DEFAULT_SLUG}_ERROR`:
      return {
        ...state,
        defaultSlug: {
          ...state.defaultSlug,
          error: action.error,
          loading: false,
        },
      };
    case `${CPM_GET_CARPARK_DEFAULT_SLUG}_SUCCESS`:
      return {
        ...state,
        defaultSlug: {
          ...state.defaultSlug,
          loading: false,
          result: deserializeSlug(action.data.getCarpark.slug),
        },
      };
    default:
      return state;
  }
};

export default carparkReducer;



// WEBPACK FOOTER //
// ./reducers/cpm/carpark/carpark.js